import * as React from "react";
import styled, { css } from "styled-components";
import { useHistory } from "react-router-dom";

import { Button } from "src/library/components/Button";
import Icon from "src/library/components/Icon/Icon";
import { Text } from "src/library/components/Text";
import { breakpoint } from "src/library/utils/styled-media-queries";
import { Icons } from "src/library/components/Icon/types";
import {
  IOrderAuditLog,
  OrderAuditLogChangeType
} from "src/core/orders/api/orders.api";
import OrderStatus from "src/core/orders/components/OrderStatus";
import { formatPrice } from "src/core/prices/utils";

interface AuditLogActionProps {
    auditLog: IOrderAuditLog;
}

/**
 * Component to display
 *
 */
export const AuditLogAction: React.ReactFCWithChildren<AuditLogActionProps> = ({ auditLog }) => {
  const history = useHistory();

  /**
   * Gets the log text from from the full data
   * basedon the log type
   */
  const Actions = React.useMemo(() => {
    switch (auditLog.type) {
      case OrderAuditLogChangeType.AddProduct:
        return (
          <LogDetailBox type="after">
            {(auditLog as IOrderAuditLog<OrderAuditLogChangeType.AddProduct>).fullData.quantity}
          </LogDetailBox>
        );

      case OrderAuditLogChangeType.QuantityChange: {
        const typedAuditLog = (auditLog as IOrderAuditLog<OrderAuditLogChangeType.QuantityChange>);

        return (
          <>
            <LogDetailBox type="before">
              {typedAuditLog.fullData.fromQuantity}
            </LogDetailBox>

            <Icon icon={Icons.CHEVRONRIGHT} />

            <LogDetailBox type="after">
              {typedAuditLog.fullData.toQuantity}
            </LogDetailBox>
          </>
        );
      }

      case OrderAuditLogChangeType.OrderTypeChange:
        return (
          <>
            <LogDetailBox type="before">
              {(auditLog as IOrderAuditLog<OrderAuditLogChangeType.OrderTypeChange>).fullData.fromOrderType}
            </LogDetailBox>

            <Icon icon={Icons.CHEVRONRIGHT} />

            <LogDetailBox type="after">
              {(auditLog as IOrderAuditLog<OrderAuditLogChangeType.OrderTypeChange>).fullData.toOrderType}
            </LogDetailBox>
          </>
        );

      case OrderAuditLogChangeType.StatusChange: {
        const typedAuditLog = (auditLog as IOrderAuditLog<OrderAuditLogChangeType.StatusChange>);

        return (
          <>
            <LogDetailBox type="before">
              {typedAuditLog.fullData.fromOrderStatus.name}
            </LogDetailBox>

            <Icon icon={Icons.CHEVRONRIGHT} />

            <LogDetailBox type="after">
              <OrderStatus
                status={typedAuditLog.fullData.toOrderStatus}
                orderId={auditLog.orderId}
                tooltipPlace="left"
              />
            </LogDetailBox>
          </>
        );
      }

      case OrderAuditLogChangeType.PriceChange: {
        const typedAuditLog = (auditLog as IOrderAuditLog<OrderAuditLogChangeType.PriceChange>);

        return (
          <>
            <LogDetailBox type="before">
              {`${formatPrice(typedAuditLog.fullData.fromPrice)}`}
            </LogDetailBox>

            <Icon icon={Icons.CHEVRONRIGHT} />

            <LogDetailBox type="after">
              {`${formatPrice(typedAuditLog.fullData.toPrice)}`}
            </LogDetailBox>
          </>
        );
      }

      case OrderAuditLogChangeType.OrderCreated: {
        const typedAuditLog = (auditLog as IOrderAuditLog<OrderAuditLogChangeType.OrderCreated>);

        if (typedAuditLog.fullData.fromOrderId) {
          return (
            <>
              <Button onClick={() => history.push(`/orders/${typedAuditLog.fullData.fromOrderId}`)}>
                View original
              </Button>
            </>
          );
        }

        return <></>;
      }

      case OrderAuditLogChangeType.ProductReplacement:
      case OrderAuditLogChangeType.ProductSubstitution: {
        const typedAuditLog = (auditLog as IOrderAuditLog<OrderAuditLogChangeType.ProductReplacement | OrderAuditLogChangeType.ProductSubstitution>);

        return (
          <>
            {typedAuditLog.fullData.quantity && (
              <LogDetailBox type="after">
                QTY
                {" "}

                {typedAuditLog.fullData.quantity}
              </LogDetailBox>
            )}

            <Text
              className="times"
              color="secondary"
            >
              {" x "}
            </Text>

            {typedAuditLog.fullData.toProduct ? (
              <LogDetailBox type="after">
                {typedAuditLog.fullData.toProduct.description}

                {" "}
                (

                {typedAuditLog.fullData.toProduct.partNumber}
                )

              </LogDetailBox>
            ) : (
              <LogDetailBox type="after">
                Removed
              </LogDetailBox>
            )}
          </>
        );
      }

      case OrderAuditLogChangeType.Message: {
        const typedAuditLog = (auditLog as IOrderAuditLog<OrderAuditLogChangeType.Message>);

        return (
          <LogDetailBox type="after">
            {typedAuditLog.fullData.text}
          </LogDetailBox>
        );
      }
      case OrderAuditLogChangeType.RemoveProduct:
      default:
        return <></>;
    }
  }, [ auditLog, history ]);

  return (
    <AuditLogActionWrapper>
      {Actions}
    </AuditLogActionWrapper>
  );
};

const AuditLogActionWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    

    > div {
      margin-top: ${theme.spacing.margins.sm};
    }
 
    svg, .times {
      display: none;
    }

    ${breakpoint("sm", theme)} {
      flex-direction: row;
      align-items: center;
      margin-top: ${theme.spacing.margins.sm};

      > div {
        margin-top: 0;
      }

      svg, .times {
        display: initial;
        margin: 0 ${theme.spacing.margins.md};
      }
  
    }

    ${breakpoint("lg", theme)} {
      margin-top: 0;
    }
  `}
`;

interface LogDetailBoxProps {
  type: "before" | "after";
}

const LogDetailBox = styled.div<LogDetailBoxProps>`
  ${({ type, theme }) => css`
    padding: ${theme.spacing.padding.sm};
    background: ${theme.colors.lightGrey};
    opacity: ${type === "before" ? 0.6 : 1};
    font-weight: bold;
    font-size: ${theme.fonts.sizes.p};
    text-align: center;

    ${breakpoint("sm", theme)} {
      font-size: ${theme.fonts.sizes.h4};
    }
  `}
`;
